'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Goal, H, Hl, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/linear-extra'

const EigenExplorer = dynamic(() => import('../_components/widgets/TensorWidgets').then(m => m.EigenExplorer), { ssr: false })
const PCAViz = dynamic(() => import('../_components/widgets/TensorWidgets').then(m => m.PCAViz), { ssr: false })

export default function EigenvectorsAndPCA() {
  return (
    <Lesson>
      <Step gate="goal-eigen">
        <H>Directions that don’t turn</H>
        <P>
          You’ve seen that a matrix moves space around: it stretches, squashes, shears and rotates. Most arrows get pointed in a
          new direction. But many matrices have a few special directions that come out pointing exactly the same way, only
          longer or shorter.
        </P>
        <P>
          Those are the <Hl c="green">eigenvectors</Hl> (from German <em>eigen</em>, “own”: the matrix’s own directions). How
          much each one gets stretched is its <strong>eigenvalue</strong>, written <M>{'\\lambda'}</M> (“lambda”).
        </P>
        <MB>{'A\\,\\blue{\\mathbf{v}} = \\lambda\\,\\blue{\\mathbf{v}}'}</MB>
        <Goal id="goal-eigen" task={<>Drag v until the matrix only stretches it: the orange arrow Av must line up with v.</>}>
          {onReach => <EigenExplorer onGoal={onReach} />}
        </Goal>
        <Callout kind="try">
          Switch to “rotate 90°”. Can you find any direction that isn’t turned? Then try the shear: how many eigen-directions
          does it have?
        </Callout>
      </Step>

      <Step gate="q-eigen">
        <H>Reading eigenvalues</H>
        <Quiz
          id="q-eigen"
          question="A matrix has an eigenvector with eigenvalue 0.5. What happens to arrows pointing that way?"
          options={[
            { label: 'They keep their direction and get half as long', correct: true },
            { label: 'They turn by half a turn', why: 'Eigenvectors don’t turn at all — that’s the whole point.' },
            { label: 'They double in length', why: 'That would be eigenvalue 2. A factor of 0.5 halves them.' },
          ]}
          explain="A negative eigenvalue flips the arrow to point backwards (still along the same line). Eigenvalue 0 squashes that direction flat."
        />
        <Exercise id="eigen-check" title="Spot them, then find them" {...EX.eigenCheck} />
        <P>
          Look at the output of that last loop. Multiplying by <M>A</M> over and over pulls <em>any</em> starting arrow towards
          the direction that gets stretched the most. That simple trick is called <strong>power iteration</strong>.
        </P>
      </Step>

      <Step gate="power-iteration">
        <H>Power iteration</H>
        <Exercise
          id="power-iteration"
          title="powerIteration and eigenvalue"
          {...EX.powerIteration}
          hints={[
            <>
              Inside a loop: <C>{'const Av = mat.apply(A, v)'}</C>, then <C>{'v = vec.scale(Av, 1 / vec.norm(Av))'}</C>.
            </>,
            <>
              <C>{'return vec.dot(v, mat.apply(A, v))'}</C>
            </>,
          ]}
        />
        <Callout kind="note">
          Google’s first ranking algorithm, PageRank, was power iteration on a gigantic matrix of links between web pages. The
          top eigenvector gave every page its importance score.
        </Callout>
      </Step>

      <Step gate="goal-pca">
        <H>Where does the data spread?</H>
        <P>
          Now the payoff. Real data usually has more numbers per example than it really needs. A 14 × 14 image of a digit is 196
          numbers, but most possible images look nothing like digits. Is there a smaller set of directions that captures most of
          what’s going on?
        </P>
        <P>
          Find the line through this cloud that keeps as much of the spread as possible. Keeping only the position along that
          line throws away the least information.
        </P>
        <Goal id="goal-pca" task={<>Turn the line until the spread along it (the variance) is as big as it can get.</>}>
          {onReach => <PCAViz onGoal={onReach} />}
        </Goal>
      </Step>

      <Step gate="q-pca">
        <H>PCA: eigenvectors of the spread</H>
        <P>
          The direction you just found is the <strong>first principal component</strong>. The beautiful fact: it’s the top
          eigenvector of the data’s <strong>covariance matrix</strong>, the matrix that records how every pair of features
          varies together.
        </P>
        <MB>{'C = \\frac{1}{n}\\,X_c^{\\top} X_c \\qquad X_c = X \\text{ with every column centred}'}</MB>
        <P>
          The next eigenvector (at right angles to the first) is the second component, and so on. Keep the top few, and you’ve
          compressed your data while keeping most of its structure. That’s <strong>Principal Component Analysis</strong>.
        </P>
        <Quiz
          id="q-pca"
          question="The eigenvalue of a principal component equals…"
          options={[
            { label: 'the variance (spread) of the data along that direction', correct: true },
            { label: 'how many data points lie on that line', why: 'Hardly any points lie exactly on the line. It measures spread.' },
            { label: 'the angle of the direction', why: 'The direction is the eigenvector itself; the eigenvalue is a size.' },
          ]}
          explain="So the eigenvalues tell you how much each component is worth. If the first two add up to 90% of the total, two numbers per example tell you 90% of the story."
        />
      </Step>

      <Step gate="pca-digits">
        <H>Squash 196 numbers into 2</H>
        <P>
          Time to run PCA for real. You’ll build the covariance matrix, pull out the top components with power iteration, and
          project 300 handwritten digits from 196 dimensions down to 2. Nobody tells PCA what the digits are. See if similar
          digits end up in the same places anyway.
        </P>
        <Exercise
          id="pca-digits" timeoutMs={60000}
          title="covariance, pca, project"
          {...EX.pcaDigits}
          hints={[
            <>
              Centre the columns (like <C>center</C> from the tensors lesson), then{' '}
              <C>{'mat.scale(mat.mul(mat.transpose(Xc), Xc), 1 / X.length)'}</C>.
            </>,
            <>
              pca: loop k times. <C>{'const v = powerIteration(C)'}</C>, <C>{'const lambda = vec.dot(v, mat.apply(C, v))'}</C>,
              push v, then <C>{'C = C.map((row, i) => row.map((x, j) => x - lambda * v[i] * v[j]))'}</C>.
            </>,
            <>
              project: centre X, then for each row <C>{'comps.map(c => vec.dot(row, c))'}</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          You’ll see PCA again in Module 8: word embeddings have hundreds of dimensions, and the 3-D “map of meaning” there is
          drawn by projecting them onto their top principal components.
        </Callout>
      </Step>
    </Lesson>
  )
}
